import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  UserPlus, X, RotateCw, ShieldCheck, Trash2, Search,
  Sticker, Sparkles, Copy, MessageCircle, Share2, Ban, UserCog,
} from "lucide-react";

const PLAN_LABEL = { monthly: "Mensal", annual: "Anual" };
const STATUS_COLOR = {
  active: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  expired: "bg-red-500/15 text-red-700 border-red-500/30",
  cancelled: "bg-slate-400/15 text-slate-600 border-slate-400/30",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
};

// Estilos derivados (evitam ternárias aninhadas no JSX)
function expiryTextClass({ expired, expiringSoon }) {
  if (expired) return "text-red-600 font-semibold";
  if (expiringSoon) return "text-amber-600 font-semibold";
  return "text-slate-400";
}
function planButtonClass(selected, color) {
  if (!selected) return "border-slate-200 hover:border-slate-300";
  return color === "purple"
    ? "border-purple-500 bg-purple-50"
    : "border-blue-500 bg-blue-50";
}

export default function UpClubAdmin() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [plans, setPlans] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/upclub/members")
      .then((r) => setMembers(r.data || []))
      .catch((e) => {
        console.error("UpClub load members:", e);
        toast.error("Erro ao carregar membros");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/public/upclub/plans")
      .then((r) => setPlans(r.data))
      .catch((e) => console.error("UpClub load plans:", e));
  }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return members;
    return members.filter((m) =>
      (m.client_name || "").toLowerCase().includes(needle) ||
      (m.phone || "").toLowerCase().includes(needle) ||
      (m.email || "").toLowerCase().includes(needle)
    );
  }, [members, q]);

  const stats = useMemo(() => {
    const active = members.filter((m) => m.status === "active").length;
    const monthly = members.filter((m) => m.plan_type === "monthly").length;
    const annual = members.filter((m) => m.plan_type === "annual").length;
    const revenue =
      monthly * 14.9 + annual * (99.9 / 12); // MRR aproximado
    return { active, monthly, annual, revenue };
  }, [members]);

  const registerFilm = async (m) => {
    try {
      const { data } = await api.post(`/upclub/members/${m.id}/register-film`);
      setMembers((cur) => cur.map((x) => (x.id === m.id ? data : x)));
      toast.success(`Troca de película registrada — ${m.client_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar");
    }
  };

  const renew = async (m) => {
    if (!window.confirm(`Renovar assinatura de ${m.client_name}?`)) return;
    try {
      const { data } = await api.post(`/upclub/members/${m.id}/renew`);
      setMembers((cur) => cur.map((x) => (x.id === m.id ? data : x)));
      toast.success("Assinatura renovada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renovar");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remover ${m.client_name} do UP Club?`)) return;
    try {
      await api.delete(`/upclub/members/${m.id}`);
      setMembers((cur) => cur.filter((x) => x.id !== m.id));
      toast.success("Membro removido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  const cancelMp = async (m) => {
    if (!window.confirm(
      `Cancelar a cobrança recorrente de ${m.client_name} no Mercado Pago?\n\n` +
      `O cliente vai parar de ser cobrado mas continua com os benefícios até ${m.expires_at || "o fim do período pago"}.`
    )) return;
    try {
      const { data } = await api.post(`/upclub/members/${m.id}/cancel-mp`);
      setMembers((cur) => cur.map((x) => (x.id === m.id ? data : x)));
      toast.success("Assinatura cancelada no Mercado Pago");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar");
    }
  };

  const changeStatus = async (m, newStatus) => {
    try {
      const { data } = await api.patch(`/upclub/members/${m.id}`, { status: newStatus });
      setMembers((cur) => cur.map((x) => (x.id === m.id ? data : x)));
      toast.success("Status atualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const copyLink = (plan) => {
    // Link interno UP Club — abre a landing dedicada com o plano pré-selecionado
    // (o modal abre automaticamente). Muito melhor que o link direto do MP porque
    // (a) passa pelo webhook auto-ativação, (b) captura os dados do cliente,
    // (c) mostra os benefícios antes.
    const base = window.location.origin;
    const url = `${base}/upclub?plan=${plan}`;
    const label = plan === "monthly" ? "mensal" : "anual";
    try {
      navigator.clipboard.writeText(url);
      toast.success(`Link ${label} copiado! Mande no WhatsApp.`);
    } catch (e) {
      console.error("clipboard error:", e);
      window.prompt("Copie o link abaixo:", url);
    }
  };

  const shareWhatsApp = (plan) => {
    const base = window.location.origin;
    const url = `${base}/upclub?plan=${plan}`;
    const price = plan === "annual" ? "R$ 99,90/ano" : "R$ 14,90/mês";
    const text = encodeURIComponent(
      `Olá! Conheça o *UP Club* — ${price} 🎉\n\n` +
      `✨ Película Hidrogel HD grátis a cada 3 meses\n` +
      `✨ 3 meses extras de garantia\n` +
      `✨ 10% off em serviços\n` +
      `✨ 15% off em acessórios\n` +
      `✨ 5% off em celulares novos\n\n` +
      `Assine em: ${url}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  // Link do Portal do Cliente (Minha Conta UP Club)
  const copyManageLink = () => {
    const url = `${window.location.origin}/upclub/manage`;
    try {
      navigator.clipboard.writeText(url);
      toast.success("Link do Portal do Cliente copiado!");
    } catch (e) {
      console.error("clipboard error:", e);
      window.prompt("Copie o link abaixo:", url);
    }
  };

  const shareManageWhatsApp = (member = null) => {
    const url = `${window.location.origin}/upclub/manage`;
    const greeting = member?.client_name
      ? `Olá, ${member.client_name.split(" ")[0]}!`
      : "Olá!";
    const text = encodeURIComponent(
      `${greeting} Aqui é da *UP CEL Quilombo* 👋\n\n` +
      `Você já pode acessar sua *Minha Conta UP Club* para:\n` +
      `✅ Ver os detalhes da sua assinatura\n` +
      `✅ Consultar o histórico de trocas de película\n` +
      `✅ Ver a próxima cobrança\n` +
      `✅ Cancelar quando quiser\n\n` +
      `👉 ${url}\n\n` +
      `É só entrar com seu telefone, e-mail ou CPF.`
    );
    // Se recebeu o membro, abre já com o número dele. Senão, WhatsApp Web pede o contato.
    const digits = (member?.phone || "").replace(/\D/g, "");
    const waUrl = digits
      ? `https://wa.me/${digits}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(waUrl, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="upclub-admin-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-600 font-semibold mb-1">
            Assinatura
          </div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-2">
            UP Club <Sparkles className="text-indigo-500" size={26} />
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Gerencie membros, renovações e trocas de película.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex flex-col gap-1">
            <button
              onClick={() => copyLink("monthly")}
              data-testid="upclub-copy-monthly"
              className="flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs px-3 py-2 rounded-sm font-semibold"
              title="Copiar link do plano mensal"
            >
              <Copy size={12} /> Link Mensal
            </button>
            <button
              onClick={() => shareWhatsApp("monthly")}
              data-testid="upclub-wa-monthly"
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 py-1.5 rounded-sm font-semibold"
              title="Enviar pelo WhatsApp"
            >
              <Share2 size={11} /> WhatsApp
            </button>
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => copyLink("annual")}
              data-testid="upclub-copy-annual"
              className="flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs px-3 py-2 rounded-sm font-semibold"
              title="Copiar link do plano anual"
            >
              <Copy size={12} /> Link Anual
            </button>
            <button
              onClick={() => shareWhatsApp("annual")}
              data-testid="upclub-wa-annual"
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 py-1.5 rounded-sm font-semibold"
              title="Enviar pelo WhatsApp"
            >
              <Share2 size={11} /> WhatsApp
            </button>
          </div>
          <div className="flex flex-col gap-1 border-l border-slate-200 pl-2 ml-1">
            <button
              onClick={copyManageLink}
              data-testid="upclub-copy-manage"
              className="flex items-center gap-1.5 bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 text-xs px-3 py-2 rounded-sm font-semibold"
              title="Copiar link do Portal do Cliente (Minha Conta UP Club)"
            >
              <UserCog size={12} /> Portal do Cliente
            </button>
            <button
              onClick={() => shareManageWhatsApp()}
              data-testid="upclub-wa-manage"
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs px-3 py-1.5 rounded-sm font-semibold"
              title="Enviar link Minha Conta pelo WhatsApp"
            >
              <Share2 size={11} /> WhatsApp
            </button>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="upclub-add-btn"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-sm text-sm font-semibold shadow-lg shadow-indigo-500/30 self-start"
          >
            <UserPlus size={16} /> Novo Membro
          </button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Ativos" value={stats.active} accent="emerald" />
        <StatCard label="Mensais" value={stats.monthly} accent="blue" />
        <StatCard label="Anuais" value={stats.annual} accent="purple" />
        <StatCard
          label="Receita ~MRR"
          value={`R$ ${stats.revenue.toFixed(2).replace(".", ",")}`}
          accent="indigo"
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, telefone ou email..."
          data-testid="upclub-search-input"
          className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="p-3">Cliente</th>
                <th className="p-3">Plano</th>
                <th className="p-3">Status</th>
                <th className="p-3">Última Película</th>
                <th className="p-3">Vence em</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">Carregando...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                  Nenhum membro. Clique em "Novo Membro" para adicionar.
                </td></tr>
              )}
              {filtered.map((m) => {
                const dLeft = daysUntil(m.expires_at);
                const expiringSoon = dLeft !== null && dLeft <= 7 && dLeft > 0;
                const expired = dLeft !== null && dLeft <= 0;
                return (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`upclub-row-${m.id}`}>
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{m.client_name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <span>{m.phone}</span>
                        {m.phone && (
                          <a
                            href={`https://wa.me/${m.phone.replace(/\D/g, "")}`}
                            target="_blank" rel="noreferrer"
                            className="text-emerald-600 hover:text-emerald-500"
                            title="Abrir no WhatsApp"
                          >
                            <MessageCircle size={12} />
                          </a>
                        )}
                      </div>
                      {m.email && <div className="text-xs text-slate-400">{m.email}</div>}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                        m.plan_type === "annual"
                          ? "bg-purple-500/10 text-purple-700 border-purple-400/30"
                          : "bg-blue-500/10 text-blue-700 border-blue-400/30"
                      }`}>
                        {PLAN_LABEL[m.plan_type] || m.plan_type}
                      </span>
                    </td>
                    <td className="p-3">
                      <select
                        value={m.status}
                        onChange={(e) => changeStatus(m, e.target.value)}
                        data-testid={`upclub-status-${m.id}`}
                        className={`text-xs font-semibold px-2 py-1 rounded-full border cursor-pointer focus:outline-none ${STATUS_COLOR[m.status] || STATUS_COLOR.active}`}
                      >
                        <option value="active">Ativo</option>
                        <option value="pending">Pendente</option>
                        <option value="expired">Expirado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </td>
                    <td className="p-3 text-slate-600 font-mono text-xs">
                      {fmtDate(m.last_film_exchange)}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-mono">{fmtDate(m.expires_at)}</div>
                      {dLeft !== null && (
                        <div className={`text-[10px] mt-0.5 ${expiryTextClass({ expired, expiringSoon })}`}>
                          {expired ? "Vencido" : `${dLeft} dia${dLeft === 1 ? "" : "s"}`}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <IconBtn onClick={() => registerFilm(m)} title="Registrar troca de película" testid={`upclub-film-${m.id}`}>
                          <Sticker size={14} />
                        </IconBtn>
                        <IconBtn onClick={() => shareManageWhatsApp(m)} title="Enviar link Minha Conta UP Club no WhatsApp deste cliente" testid={`upclub-send-portal-${m.id}`} accent="indigo">
                          <UserCog size={14} />
                        </IconBtn>
                        <IconBtn onClick={() => renew(m)} title="Renovar assinatura (adiciona tempo)" testid={`upclub-renew-${m.id}`} accent="indigo">
                          <RotateCw size={14} />
                        </IconBtn>
                        {m.mp_preapproval_id && m.status !== "cancelled" && (
                          <IconBtn onClick={() => cancelMp(m)} title="Cancelar cobrança recorrente no Mercado Pago" testid={`upclub-cancel-mp-${m.id}`} accent="amber">
                            <Ban size={14} />
                          </IconBtn>
                        )}
                        <IconBtn onClick={() => remove(m)} title="Remover do clube" testid={`upclub-remove-${m.id}`} accent="red">
                          <Trash2 size={14} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onCreated={(m) => {
            setMembers((cur) => [m, ...cur]);
            setShowAdd(false);
            toast.success(`${m.client_name} adicionado ao UP Club!`);
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent = "slate" }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`border rounded-sm p-4 ${colors[accent] || colors.slate}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, accent = "slate" }) {
  const colors = {
    slate: "border-slate-300 text-slate-600 hover:bg-slate-100",
    indigo: "border-indigo-300 text-indigo-600 hover:bg-indigo-50",
    amber: "border-amber-300 text-amber-600 hover:bg-amber-50",
    red: "border-red-300 text-red-600 hover:bg-red-50",
  };
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 border rounded-sm ${colors[accent]}`}
    >
      {children}
    </button>
  );
}

function AddMemberModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", plan_type: "monthly", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/upclub/members", payload);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-sm w-full max-w-md p-6 shadow-2xl"
        data-testid="upclub-add-modal"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-indigo-600 font-semibold">UP Club</div>
            <h2 className="font-display text-2xl font-bold">Novo Membro</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="upclub-add-close">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Nome do cliente *">
            <input
              type="text" required value={form.client_name}
              onChange={upd("client_name")}
              data-testid="upclub-input-name"
              className="input"
              placeholder="Ex: Jean Carlos Bonetti"
            />
          </Field>
          <Field label="WhatsApp *">
            <input
              type="tel" required value={form.phone}
              onChange={upd("phone")}
              data-testid="upclub-input-phone"
              className="input"
              placeholder="49 99999-9999"
            />
          </Field>
          <Field label="Email (opcional)">
            <input
              type="email" value={form.email}
              onChange={upd("email")}
              data-testid="upclub-input-email"
              className="input"
              placeholder="cliente@email.com"
            />
          </Field>
          <Field label="Plano *">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "monthly", label: "Mensal", price: "R$ 14,90/mês", color: "blue" },
                { v: "annual", label: "Anual", price: "R$ 99,90/ano", color: "purple" },
              ].map((p) => (
                <button
                  type="button"
                  key={p.v}
                  onClick={() => setForm((f) => ({ ...f, plan_type: p.v }))}
                  data-testid={`upclub-plan-${p.v}`}
                  className={`p-3 rounded-sm border-2 text-left transition-all ${planButtonClass(form.plan_type === p.v, p.color)}`}
                >
                  <div className="font-semibold text-sm text-slate-800">{p.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.price}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Observações">
            <textarea
              value={form.notes} onChange={upd("notes")}
              data-testid="upclub-input-notes"
              className="input min-h-[60px]"
              placeholder="Ex: pagou via PIX no dia 10/02..."
            />
          </Field>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-sm"
          >
            Cancelar
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="upclub-save-btn"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white px-5 py-2 rounded-sm text-sm font-semibold"
          >
            <ShieldCheck size={14} />
            {saving ? "Salvando..." : "Adicionar ao Club"}
          </button>
        </div>
        <style>{`
          .input {
            width: 100%; padding: 0.5rem 0.75rem;
            border: 1px solid #cbd5e1; border-radius: 2px;
            font-size: 0.875rem; background: white; outline: none;
            transition: border-color 0.15s, box-shadow 0.15s;
          }
          .input:focus {
            border-color: #6366f1;
            box-shadow: 0 0 0 2px rgba(99,102,241,0.2);
          }
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
